

import React, { useContext, useState, useMemo } from 'react';
import { PageTitle, Card, EmptyState, Button } from '../components/common';
import { AuthContext } from '../contexts/AuthContext';
import { mockOrders, mockCourses } from '../mockData';
import { Order } from '../types';
import { Link } from 'react-router-dom';

// Icons
const SearchIcon = () => <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>;
const ViewIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const XIcon = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path></svg>;
const ChevronLeftIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const ChevronRightIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;

const ORDERS_PER_PAGE = 10;

const StatusBadge: React.FC<{ status: Order['status'] }> = ({ status }) => {
    const styles = {
        'Completed': 'bg-green-100 text-green-800',
        'Pending': 'bg-blue-100 text-blue-800',
        'Failed': 'bg-red-100 text-red-800',
    };
    const dotStyles = {
        'Completed': 'bg-green-500',
        'Pending': 'bg-blue-500',
        'Failed': 'bg-red-500',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${styles[status]}`}>
            <span className={`w-2 h-2 mr-1.5 rounded-full ${dotStyles[status]}`}></span>
            {status}
        </span>
    );
};

const InvoicePreviewModal: React.FC<{
    order: Order | null;
    onClose: () => void;
    generateInvoiceContent: (order: Order) => string;
}> = ({ order, onClose, generateInvoiceContent }) => {
    if (!order) return null;
    
    // Remove the print script for the iframe view
    const invoiceHtmlForView = generateInvoiceContent(order)
        .replace('<script>window.onload = function() { window.print(); window.close(); }</script>', '');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800">Invoice Preview: #{order.id}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                </header>
                <div className="p-4 flex-1">
                    <iframe
                        srcDoc={invoiceHtmlForView}
                        title={`Invoice for Order ${order.id}`}
                        className="w-full h-full border rounded-md"
                    />
                </div>
                <footer className="p-4 border-t flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </footer>
            </div>
        </div>
    );
};

const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (page: number) => void }> = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
        <div className="flex flex-wrap justify-between items-center mt-6 px-6 pb-4 gap-4">
            <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
            </p>
            <nav className="flex items-center space-x-1">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Previous page"
                >
                    <ChevronLeftIcon />
                </button>
                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => onPageChange(number)}
                        className={`w-9 h-9 text-sm font-medium rounded-full flex items-center justify-center transition-colors ${currentPage === number ? 'bg-primary text-white shadow-sm' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        aria-current={currentPage === number ? 'page' : undefined}
                    >
                        {number}
                    </button>
                ))}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Next page"
                >
                    <ChevronRightIcon />
                </button>
            </nav>
        </div>
    );
};

const OrderHistoryPage: React.FC = () => {
    const auth = React.useContext(AuthContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedOrderForView, setSelectedOrderForView] = useState<Order | null>(null);

    const userOrders = useMemo(() => mockOrders.filter(order => order.userId === auth?.currentUser?.id), [auth?.currentUser?.id]);

    const filteredOrders = useMemo(() => {
        return userOrders
            .filter(order => statusFilter === 'All' || order.status === statusFilter)
            .filter(order => order.id.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [userOrders, statusFilter, searchTerm]);

    const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

    const paginatedOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ORDERS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    const getCourseName = (courseId: string) => mockCourses.find(c => c.id === courseId)?.title || `Course ID: ${courseId}`;
    
    const generateInvoiceContent = (order: Order) => {
        return `
            <html>
                <head>
                    <title>Invoice for Order ${order.id}</title>
                    <style>
                        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; margin: 2em; color: #333; }
                        .container { max-width: 800px; margin: auto; padding: 20px; border: 1px solid #eee; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
                        h1 { color: #ef4444; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                        th { background-color: #f9f9f9; }
                        .total { font-weight: bold; }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <h1>Invoice</h1>
                        <p><strong>Order ID:</strong> #${order.id}</p>
                        <p><strong>Date:</strong> ${new Date(order.date).toLocaleDateString()}</p>
                        <p><strong>Status:</strong> ${order.status}</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>Item(s)</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${order.courseIds.map(id => `<tr><td>${getCourseName(id)}</td><td>-</td></tr>`).join('')}
                                 <tr class="total">
                                    <td><strong>Total</strong></td>
                                    <td><strong>$${order.amount.toFixed(2)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                         <p style="margin-top: 20px; font-size: 0.9em; color: #555;">Thank you for your business!</p>
                    </div>
                </body>
            </html>
        `;
    };

    const handleDownloadPdf = (order: Order) => {
        const invoiceHtml = generateInvoiceContent(order);
        const win = window.open("", "_blank");
        if (win) {
            win.document.write(invoiceHtml);
            win.document.write('<script>window.onload = function() { window.print(); window.close(); }</script>');
            win.document.close();
        }
    };

    if (userOrders.length === 0) {
        return (
            <div>
                <PageTitle title="Order History" />
                <EmptyState 
                    title="No Orders Yet"
                    message="Your order history will appear here once you make a purchase."
                    action={<Button as={Link} to="/courses">Explore Courses</Button>}
                />
            </div>
        )
    }

    return (
        <div>
            <PageTitle title="Order History" />
            
            <Card className="overflow-hidden">
                <div className="p-4 flex flex-wrap justify-between items-center gap-4 border-b">
                    <div className="w-full sm:w-auto">
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            className="p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Completed">Completed</option>
                            <option value="Pending">Pending</option>
                            <option value="Failed">Failed</option>
                        </select>
                    </div>
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search by Order ID..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 font-semibold">
                            <tr>
                                <th scope="col" className="px-6 py-4">Order ID</th>
                                <th scope="col" className="px-6 py-4">Course Name</th>
                                <th scope="col" className="px-6 py-4">Date</th>
                                <th scope="col" className="px-6 py-4">Amount</th>
                                <th scope="col" className="px-6 py-4">Status</th>
                                <th scope="col" className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedOrders.length > 0 ? (
                                paginatedOrders.map((order) => {
                                    const courseNames = order.courseIds.map(getCourseName).join(', ');
                                    return (
                                        <tr key={order.id} className="bg-white border-b last:border-b-0 hover:bg-gray-50">
                                            <td className="px-6 py-4 font-bold text-blue-600">#{order.id.replace('ORD', '')}</td>
                                            <td className="px-6 py-4 text-gray-800 font-medium">{courseNames}</td>
                                            <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{new Date(order.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                            <td className="px-6 py-4 font-semibold text-gray-700">${order.amount.toFixed(2)}</td>
                                            <td className="px-6 py-4"><StatusBadge status={order.status} /></td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-start space-x-4 text-gray-500">
                                                     <button onClick={() => setSelectedOrderForView(order)} className="hover:text-primary" aria-label={`View invoice for ${order.id}`}>
                                                        <ViewIcon />
                                                    </button>
                                                    <button onClick={() => handleDownloadPdf(order)} className="hover:text-primary" aria-label={`Download PDF for ${order.id}`}>
                                                        <DownloadIcon />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-10">
                                        <p className="text-gray-500">No orders match your criteria.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                )}
            </Card>

            <InvoicePreviewModal 
                order={selectedOrderForView}
                onClose={() => setSelectedOrderForView(null)}
                generateInvoiceContent={generateInvoiceContent}
            />
        </div>
    );
};

export default OrderHistoryPage;
